import React from 'react';
import { Prospect, ProposalItem } from '../../shared/types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { CheckCircle2, MessageSquare, Sparkles, HelpCircle, ArrowRight } from 'lucide-react';

interface PricingSectionProps {
  prospect: Prospect;
  items?: ProposalItem[];
}

export const PricingSection: React.FC<PricingSectionProps> = ({ prospect }) => {
  const whatsappUrl = `https://wa.me/${prospect.whatsapp}?text=${encodeURIComponent(
    `Olá YouMachine! Analisei os planos para a ${prospect.trade_name} e quero agendar o início.`
  )}`;

  return (
    <section id="precos" className="py-20 border-b border-slate-100 bg-slate-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-[var(--ym-prospect-primary)]">Planos & Investimento Comercial</span>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mt-2">
            Modelos de Contratação Transparentes
          </h2>
          <p className="text-slate-600 mt-4 text-base font-light leading-relaxed">
            Selecione o formato ideal para a <strong>{prospect.trade_name}</strong>. Oferecemos pacotes promocionais de entrada ou serviços gerenciados completos por assinatura.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          
          {/* Card 1: Pacote Promocional DO OFF AO ON */}
          <Card className="border-rose-100 bg-white flex flex-col justify-between p-8 relative shadow-sm">
            <div className="absolute -top-3 right-6 bg-rose-600 text-white text-[10px] font-black uppercase px-3 py-1 rounded-full tracking-wider shadow-lg flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Promocional Entrada
            </div>
            <div>
              <div className="flex items-center justify-between mb-4">
                <Badge variant="indigo" className="uppercase tracking-wider bg-rose-50 text-rose-600 border-rose-200">DO OFF AO ON</Badge>
                <span className="text-xs text-rose-600 font-medium">Aceleração Inicial</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-900">Pacote do Off ao On</h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Transformação digital acelerada para marcas que querem entrar na internet com presença completa.
              </p>

              <div className="my-6 py-4 border-y border-slate-100">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-black text-rose-600">R$ 990,00</span>
                  <span className="text-xs text-slate-500">/mês nos 1ºs 6 meses</span>
                </div>
                <p className="text-xs text-slate-500 font-medium mt-1">
                  Depois R$ 1.990,00/mês na renovação anual
                </p>
              </div>

              <h4 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">
                Entregáveis inclusos:
              </h4>
              <ul className="space-y-2.5 text-xs text-slate-600 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-rose-500 shrink-0" />
                  <span>Configuração de Perfis Instagram & Facebook</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-rose-500 shrink-0" />
                  <span>Site Institucional & Vitrine Virtual em {prospect.city}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-rose-500 shrink-0" />
                  <span>Google Meu Negócio Otimizado</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-rose-500 shrink-0" />
                  <span>Cadastro de 20 Produtos no Google Shopping</span>
                </li>
              </ul>
            </div>

            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="w-full">
              <Button className="w-full bg-rose-600 text-white hover:bg-rose-700 font-bold text-xs uppercase py-4">
                Garantir Pacote R$ 990/mês
              </Button>
            </a>
          </Card>

          {/* Card 2: Compra do Projeto Fechado */}
          <Card className="border-indigo-100 bg-white flex flex-col justify-between p-8 shadow-sm">
            <div>
              <div className="flex items-center justify-between mb-4">
                <Badge variant="indigo" className="uppercase tracking-wider">Modelo Compra</Badge>
                <span className="text-xs text-slate-500 font-medium">Propriedade Total</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-900">Projeto Fechado (Web / LP)</h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Desenvolvimento sob medida e entrega finalizada do código-fonte.
              </p>

              <div className="my-6 py-4 border-y border-slate-100">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-extrabold text-slate-900">R$ 2.000,00</span>
                  <span className="text-xs text-slate-500">taxa única</span>
                </div>
                <p className="text-xs text-emerald-600 font-medium mt-1">
                  + Manutenção opcional por R$ 350,00/mês
                </p>
              </div>

              <h4 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">
                O que está incluído:
              </h4>
              <ul className="space-y-2.5 text-xs text-slate-600 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>Website / Landing Page responsiva personalizada</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>Otimização para busca local (SEO em {prospect.city})</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>Integração com WhatsApp e CRM</span>
                </li>
              </ul>
            </div>

            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="w-full">
              <Button variant="outline" className="w-full text-xs font-bold uppercase py-4 border-slate-200 text-slate-900 hover:bg-slate-50">
                Contratar Projeto Fechado
              </Button>
            </a>
          </Card>

          {/* Card 3: Pacote Flexível Completo 360° */}
          <Card className="border-emerald-100 bg-emerald-50/50 flex flex-col justify-between p-8 relative shadow-sm">
            <div>
              <div className="flex items-center justify-between mb-4">
                <Badge variant="emerald" className="uppercase tracking-wider">Serviço Gerenciado 360°</Badge>
                <span className="text-xs text-emerald-600 font-medium">Ecossistema Ativo</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-900">Pacote Flexível 360°</h3>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Gestão contínua de tecnologia, aplicativo próprio e anúncios digitais.
              </p>

              <div className="my-6 py-4 border-y border-emerald-100">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-extrabold text-emerald-600">R$ 2.000,00</span>
                  <span className="text-xs text-slate-500">/ mês</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Setup inicial simplificado • Verba de tráfego paga à parte
                </p>
              </div>

              <h4 className="text-xs font-bold uppercase text-emerald-700 tracking-wider mb-3">
                Entregas mensais inclusas:
              </h4>
              <ul className="space-y-2.5 text-xs text-slate-700 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span><strong>Website + App Mobile Próprio</strong> hospedados</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span><strong>Gestão de Tráfego Pago:</strong> Meta Ads & Google</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span><strong>Social Media:</strong> 8 posts e 8 stories/mês</span>
                </li>
              </ul>
            </div>

            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="w-full">
              <Button variant="whatsapp" className="w-full text-xs font-bold uppercase py-4" icon={<MessageSquare className="w-4 h-4" />}>
                Contratar Serviço 360°
              </Button>
            </a>
          </Card>

        </div>

        {/* Esclarecimento Transparente de Direitos e Licenças */}
        <div className="p-6 rounded-2xl bg-[var(--ym-prospect-bg)] border border-[var(--ym-prospect-border)] shadow-sm">
          <div className="flex items-center gap-2 mb-3 text-[var(--ym-prospect-primary)] font-bold text-sm">
            <HelpCircle className="w-5 h-5 shrink-0" />
            Entenda com clareza o que pertence à sua empresa
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-xs text-slate-600">
            <div>
              <strong className="text-slate-900 block mb-1">Modelo Compra:</strong>
              Após a quitação integral do valor do projeto, os arquivos do site e licenças dos conteúdos pertencem à sua empresa. A manutenção opcional por R$ 350/mês pode ser mantida ou cancelada sem perda do código.
            </div>
            <div>
              <strong className="text-slate-900 block mb-1">Modelo Assinatura / Gerenciado:</strong>
              Os recursos hospedados em nossa infraestrutura permanecem ativos enquanto a mensalidade estiver em dia. Posts e vídeos veiculados em suas redes sociais permanecem em suas contas ativas.
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
